import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChakraProvider, useToast, Box, Input, Button, FormControl, FormLabel, FormErrorMessage, VStack, Heading, Container, Center, Image, Text, Divider } from '@chakra-ui/react';

function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const validateForm = () => {
    const newErrors = {};

    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        description: 'Please fix the errors in the form',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      });
      return;
    }

    setIsLoading(true);

    try {
      // API call for admin login
      console.log('Attempting login with:', { username: username.trim() });
      
      const response = await fetch(
        'https://myblrc.in/api/api.php?xAction=adminLogin',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username.trim(),
            password: password,
          }),
        }
      );

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Get response text first to handle both JSON and text errors
      const responseText = await response.text();
      console.log('Raw response text:', responseText);
      
      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Full API response:', result);
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        console.error('Response was:', responseText);
        toast({
          description: `Server returned invalid response. Please check the console for details.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
        setIsLoading(false);
        return;
      }

      // Check if response is OK
      if (!response.ok) {
        console.error('Response not OK:', result);
        const errorMessage = result?.data?.message || result?.message || `Server error (${response.status})`;
        toast({
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
        setIsLoading(false);
        return;
      }

      if (result.data?.status === 'success' || result.status === 'success') {
        // Store auth token/session
        localStorage.setItem('adminToken', result.data?.token || 'authenticated');
        localStorage.setItem('adminUsername', username.trim());
        
        console.log('Login successful, token stored');
        
        toast({
          description: 'Login successful! Redirecting...',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom',
        });

        setTimeout(() => {
          navigate('/Admin/Contacts');
        }, 500);
      } else {
        console.error('Login failed:', result);
        const errorMessage = result.data?.message || result.message || 'Invalid username or password';
        toast({
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast({
        description: `Login failed: ${error.message || 'Please check your internet connection and try again.'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ChakraProvider>
      <Box
        minH="100vh"
        bgGradient="linear(to-br, #1a202c, #2d3748)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={8}
        px={4}
      >
        <Container maxW="md">
          <Box
            bg="white"
            p={10}
            borderRadius="xl"
            boxShadow="2xl"
            border="1px solid"
            borderColor="gray.200"
          >
            <VStack spacing={6} align="stretch">
              {/* Logo Section */}
              <Center>
                <VStack spacing={3}>
                  <Box
                    w="70px"
                    h="70px"
                    bg="white"
                    borderRadius="lg"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    border="2px solid"
                    borderColor="blue.100"
                    boxShadow="sm"
                    p={2}
                  >
                    <Image
                      src="/assets/images/white-logo.svg"
                      alt="BLRC Logo"
                      maxW="50px"
                      maxH="50px"
                      objectFit="contain"
                      fallback={
                        <Text fontSize="lg" fontWeight="bold" color="blue.600" letterSpacing="0.5px">
                          BLRC
                        </Text>
                      }
                    />
                  </Box>
                  <VStack spacing={0.5}>
                    <Heading size="lg" color="gray.800" fontWeight="700" letterSpacing="-0.5px">
                      Admin Login
                    </Heading>
                    <Text color="gray.500" fontSize="xs" fontWeight="500" letterSpacing="0.3px">
                      Business Law Relief Center
                    </Text>
                  </VStack>
                </VStack>
              </Center>

              <Divider borderColor="gray.200" />

              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  <FormControl isInvalid={errors.username}>
                    <FormLabel fontWeight="600" color="gray.700" mb={2}>
                      Username
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      size="lg"
                      borderRadius="md"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'blue.300' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (errors.username) {
                          setErrors({ ...errors, username: '' });
                        }
                      }}
                      onBlur={() => {
                        if (username && username.trim().length < 3) {
                          setErrors({ ...errors, username: 'Username must be at least 3 characters' });
                        }
                      }}
                    />
                    <FormErrorMessage>{errors.username}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.password}>
                    <FormLabel fontWeight="600" color="gray.700" mb={2}>
                      Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      size="lg"
                      borderRadius="md"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'blue.300' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) {
                          setErrors({ ...errors, password: '' });
                        }
                      }}
                      onBlur={() => {
                        if (password && password.length < 6) {
                          setErrors({ ...errors, password: 'Password must be at least 6 characters' });
                        }
                      }}
                    />
                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="blue"
                    width="100%"
                    size="lg"
                    height="50px"
                    borderRadius="md"
                    fontWeight="600"
                    fontSize="md"
                    isLoading={isLoading}
                    loadingText="Logging in..."
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                </VStack>
              </form>
            </VStack>
          </Box>
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default AdminLogin;

